<!-- src/components/posts/PostPreview.vue -->
<script setup>
import { computed, defineProps } from 'vue'
import api from '@/services/api'
import { safeOpenUrl } from '@/utils/urlValidator';
import { isValidUrl } from '@/utils/sanitizer';

const props = defineProps({
  post: {
    type: Object,
    required: true
  },
  previewSize: {
    type: String,
    default: 'medium', // 'small', 'medium', 'large'
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  }
})

// Computed pour les dimensions
const dimensions = computed(() => {
  switch (props.previewSize) {
    case 'small':
      return { width: '100%', height: '150px' }
    case 'medium':
      return { width: '100%', height: '200px' }
    case 'large':
      return { width: '100%', height: '300px' }
    default:
      return { width: '100%', height: '200px' }
  }
})

// Obtenir l'URL complète du fichier
const fileUrl = computed(() => {
  if (!props.post.file?.filename) return null
  
  // Si le fichier a déjà une URL complète (peut-être depuis l'API)
  if (props.post.file.filename.startsWith('http')) {
    return props.post.file.filename
  }
  
  // Utiliser la base URL de votre instance axios configurée
  const baseURL = api?.defaults?.baseURL || 'http://localhost:8000/api'
  return `${baseURL}/uploads/${props.post.file.filename}`
})

// Vérifier si c'est une image
const isImage = computed(() => {
  const mime = props.post.file?.mimetype || ''
  return mime.startsWith('image/') || props.post.media_type === 'I'
})

// Vérifier si c'est une vidéo
const isVideo = computed(() => {
  const mime = props.post.file?.mimetype || ''
  return mime.startsWith('video/') || props.post.media_type === 'V'
})

// Vérifier si c'est un audio
const isAudio = computed(() => {
  const mime = props.post.file?.mimetype || ''
  return mime.startsWith('audio/') || props.post.media_type === 'A'
})

// Vérifier si c'est un fichier
const isFile = computed(() => {
  return props.post.media_type === 'F' && props.post.file
})

// Obtenir l'icône selon le type
const getIcon = computed(() => {
  switch (props.post.media_type) {
    case 'T': return 'ri-file-text-line'
    case 'V': return 'ri-video-line'
    case 'A': return 'ri-music-line'
    case 'I': return 'ri-image-line'
    case 'L': return 'ri-link'
    case 'F': {
      const mime = props.post.file?.mimetype || ''
      if (mime.includes('pdf')) return 'ri-file-pdf-line'
      if (mime.includes('word')) return 'ri-file-word-line'
      if (mime.includes('excel')) return 'ri-file-excel-line'
      return 'ri-file-text-line'
    }
    default: return 'ri-file-text-line'
  }
})

// Obtenir la couleur selon le type
const getColor = computed(() => {
  switch (props.post.media_type) {
    case 'T': return 'text-secondary'
    case 'V': return 'text-danger'
    case 'A': return 'text-success'
    case 'I': return 'text-info'
    case 'L': return 'text-primary'
    case 'F': return 'text-warning'
    default: return 'text-secondary'
  }
})

// Formater la taille du fichier
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Télécharger le fichier
const downloadFile = () => {
  if (fileUrl.value) {
    const link = document.createElement('a')
    link.href = fileUrl.value
    link.download = props.post.file.real_name || props.post.file.filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

// Prévisualiser le fichier (ouvrir dans un nouvel onglet)
const previewFile = () => {
  if (fileUrl.value) {
    safeOpenUrl(fileUrl.value)
  }
}

// Fonction pour lire l'audio/vidéo
const playMedia = () => {
  if (isAudio.value || isVideo.value) {
    const mediaElement = document.querySelector(`[data-media-id="${props.post.id}"]`)
    if (mediaElement) {
      mediaElement.play()
    }
  }
}
</script>

<template>
  <div class="post-preview">
    <!-- Image Preview -->
    <div v-if="isImage && fileUrl" class="image-preview">
      <img 
        :src="fileUrl" 
        :alt="post.file?.real_name || post.title"
        class="img-fluid rounded"
        :style="dimensions"
        @click="previewFile"
      />
      <div class="preview-overlay" @click="previewFile">
        <i class="ri-eye-line preview-icon"></i>
      </div>
    </div>

    <!-- Video Preview -->
    <div v-else-if="isVideo && fileUrl" class="video-preview">
      <video 
        :src="fileUrl" 
        class="img-fluid rounded"
        :style="dimensions"
        controls
        preload="metadata"
        :data-media-id="post.id"
      >
        Your browser does not support the video tag.
      </video>
      <div class="preview-overlay" @click="previewFile">
        <i class="ri-play-circle-line preview-icon"></i>
      </div>
    </div>

    <!-- Audio Preview -->
    <div v-else-if="isAudio && fileUrl" class="audio-preview">
      <div class="audio-player rounded" :style="{ height: dimensions.height }">
        <div class="d-flex align-items-center h-100 p-3">
          <i class="ri-music-2-fill display-4 text-success me-3"></i>
          <div class="flex-grow-1">
            <h6 class="mb-1">{{ post.file?.real_name || post.title }}</h6>
            <p class="text-muted mb-2 small">{{ formatFileSize(post.file?.size) }}</p>
            <audio 
              :src="fileUrl" 
              controls 
              class="w-100"
              :data-media-id="post.id"
            ></audio>
          </div>
        </div>
      </div>
    </div>

    <!-- File Preview (PDF, Word, Excel, etc.) -->
    <div v-else-if="isFile && fileUrl" class="file-preview">
      <div class="file-preview-card rounded" :style="{ height: dimensions.height }" @click="previewFile">
        <div class="d-flex align-items-center justify-content-center h-100 p-3">
          <div class="text-center">
            <i :class="[getIcon, 'display-4', getColor]"></i>
            <h6 class="mt-2 mb-1">{{ post.file?.real_name || 'File' }}</h6>
            <p class="text-muted mb-0 small">{{ formatFileSize(post.file?.size) }}</p>
            <small class="text-muted">Click to preview</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Text Preview -->
    <div v-else-if="post.media_type === 'T'" class="text-preview">
      <div class="text-preview-content rounded" :style="{ height: dimensions.height }">
        <div class="p-3 h-100">
          <i class="ri-file-text-line display-4 text-secondary mb-3"></i>
          <p class="text-muted mb-0" v-if="post.media_content">
            {{ post.media_content.length > 150 ? post.media_content.substring(0, 150) + '...' : post.media_content }}
          </p>
          <p v-else class="text-muted mb-0">Text content</p>
        </div>
      </div>
    </div>

    <!-- Link Preview -->
    <div v-else-if="post.media_type === 'L'" class="link-preview">
      <div class="link-preview-content rounded" :style="{ height: dimensions.height }">
        <div class="p-3 h-100">
          <i class="ri-link display-4 text-primary mb-3"></i>
          <p class="text-muted mb-2" v-if="post.media_content">
            <a v-if="isValidUrl(post.media_content)" :href="post.media_content" target="_blank" rel="noopener noreferrer" class="text-decoration-none">
              {{ post.media_content.length > 50 ? post.media_content.substring(0, 50) + '...' : post.media_content }}
            </a>
            <span v-else class="text-muted">{{ post.media_content }}</span>
          </p>
          <p v-else class="text-muted mb-0">Link content</p>
        </div>
      </div>
    </div>

    <!-- Default Preview (no file) -->
    <div v-else class="default-preview">
      <div class="default-preview-content rounded" :style="{ height: dimensions.height }">
        <div class="d-flex align-items-center justify-content-center h-100">
          <div class="text-center">
            <i :class="[getIcon, 'display-4', getColor]"></i>
            <h6 class="mt-2 mb-0">No preview available</h6>
          </div>
        </div>
      </div>
    </div>

    <!-- File Info -->
    <div v-if="post.file" class="file-info mt-2">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <small class="text-muted">{{ post.file.real_name }}</small>
          <br>
          <small class="text-muted">{{ formatFileSize(post.file.size) }}</small>
        </div>
        <div class="hstack gap-1">
          <BButton 
            variant="light" 
            size="sm" 
            @click.stop="playMedia"
            v-if="isAudio || isVideo"
            :title="isAudio ? 'Play audio' : 'Play video'"
          >
            <i class="ri-play-line"></i>
          </BButton>
          <BButton 
            variant="light" 
            size="sm" 
            @click.stop="previewFile" 
            v-if="isImage || isFile"
            title="Preview"
          >
            <i class="ri-eye-line"></i>
          </BButton>
          <BButton 
            variant="light" 
            size="sm" 
            @click.stop="downloadFile"
            title="Download"
          >
            <i class="ri-download-line"></i>
          </BButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.post-preview {
  position: relative;
}

.image-preview, .video-preview {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border-radius: 0.375rem;
}

.image-preview img, .video-preview video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  border-radius: 0.375rem;
}

.image-preview:hover img {
  transform: scale(1.05);
}

.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  cursor: pointer;
  border-radius: 0.375rem;
}

.image-preview:hover .preview-overlay,
.video-preview:hover .preview-overlay {
  opacity: 1;
}

.preview-icon {
  color: white;
  font-size: 2rem;
}

.audio-player, .file-preview-card, 
.text-preview-content, .link-preview-content,
.default-preview-content {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 0.375rem;
}

.audio-player:hover, .file-preview-card:hover,
.text-preview-content:hover, .link-preview-content:hover,
.default-preview-content:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.file-preview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.audio-player audio {
  height: 40px;
  border-radius: 0.375rem;
}

.video-preview video {
  background-color: #000;
}

.audio-player {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.file-preview-card {
  background: linear-gradient(135deg, #fff9db 0%, #fff3bf 100%);
  border-color: #ffd43b;
}

.text-preview-content {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.link-preview-content {
  background: linear-gradient(135deg, #e7f5ff 0%, #d0ebff 100%);
  border-color: #339af0;
}

.default-preview-content {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}
</style>